import React, { useState, useEffect } from "react";
import LeagueModal from "./LeagueModal";
import LeagueClubsModal from "./LeagueClubsModal";
import { SearchInput, ConfirmDialog } from "../../admin";

const LeaguesTab = () => {
  const [leagues, setLeagues] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLeagueModal, setShowLeagueModal] = useState(false);
  const [showClubsModal, setShowClubsModal] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leagueToDelete, setLeagueToDelete] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchLeagues();
    fetchClubs();
  }, []);

  const fetchLeagues = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/leagues`);
      if (!response.ok) throw new Error("Failed to fetch leagues");
      const result = await response.json();
      setLeagues(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/clubs`);
      if (!response.ok) throw new Error("Failed to fetch clubs");
      const data = await response.json();
      setClubs(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddLeague = async (newLeague) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/leagues`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newLeague),
      });
      if (!response.ok) throw new Error("Failed to add league");
      const result = await response.json();
      setLeagues(prevLeagues => [...prevLeagues, result.data]);
      setShowLeagueModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditLeague = async (league) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/leagues/${league.leagueId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(league),
        }
      );
      if (!response.ok) throw new Error("Failed to update league");
      const result = await response.json();
      setLeagues(
        leagues.map((l) => (l.leagueId === league.leagueId ? result.data : l))
      );
      setShowLeagueModal(false);
      setSelectedLeague(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeague = async () => {
    if (!leagueToDelete) return;
    const league = leagueToDelete;

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/leagues/${league.leagueId}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) throw new Error("Failed to delete league");
      setLeagues((currentLeagues) => currentLeagues.filter((l) => l.leagueId !== league.leagueId));
      setLeagueToDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClubsToLeague = async (clubIds) => {
    if (!selectedLeague || !clubIds.length) return;
    
    const seasonYear = new Date().getFullYear();
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/leagues/${selectedLeague.leagueId}/clubs`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clubIds, seasonYear }),
        }
      );
      if (!response.ok) throw new Error("Failed to add clubs to league");
      await fetchLeagues();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClubsFromLeague = async (clubIds) => {
    if (!selectedLeague || !clubIds.length) return;
    
    const seasonYear = new Date().getFullYear();
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/leagues/${selectedLeague.leagueId}/clubs`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clubIds, seasonYear }),
        }
      );
      if (!response.ok) throw new Error("Failed to remove clubs from league");
      await fetchLeagues();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManageClubs = (league) => {
    setSelectedLeague(league);
    setShowClubsModal(true);
  };

  /* Derived: search filter */
  const filtered = (Array.isArray(leagues) ? leagues : []).filter((league) =>
    !search || league.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search leagues by name…"
        />
        {search && (
          <button type="button" className="btn-ghost shrink-0" onClick={() => setSearch("")}>
            Clear
          </button>
        )}
        <button
          type="button"
          className="btn-primary shrink-0 sm:ml-auto"
          onClick={() => {
            setSelectedLeague(null);
            setShowLeagueModal(true);
          }}
        >
          Add League
        </button>
      </div>

      {error && <div className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">{error}</div>}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-14 flex flex-col items-center text-gray-400">
            <div className="w-8 h-8 border-2 border-line-strong border-t-accent rounded-full animate-spin" />
            <p className="mt-4 text-sm">Loading leagues…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-base font-semibold text-white">
              {search ? "No leagues match your search" : "No leagues yet"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {search ? "Try a different name." : "Add your first league to get started."}
            </p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="table-head">
                <th className="px-3.5 py-2.5 text-left">League</th>
                <th className="px-3.5 py-2.5 text-left">Clubs</th>
                <th className="px-3.5 py-2.5 text-right sticky right-0 bg-surface-600 z-10 border-l border-line-strong">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((league) => (
                <tr key={league.leagueId} className="table-row group">
                  <td className="px-3.5 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="w-1 h-8 bg-accent rounded-full shrink-0"></span>
                      <span className="text-white font-medium">{league.name}</span>
                    </div>
                  </td>
                  <td className="px-3.5 py-2.5">
                    {league.clubs && league.clubs.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {league.clubs.map((club) => (
                          <span
                            key={club.clubId}
                            className="inline-flex items-center bg-surface-900 text-gray-200 px-2.5 py-1 rounded-full text-xs border border-line"
                          >
                            {club.logo && (
                              <img src={club.logo} alt={club.name} className="w-4 h-4 object-cover rounded-full mr-1.5" />
                            )}
                            {club.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="italic text-gray-600">No clubs</span>
                    )}
                  </td>
                  <td className="px-3.5 py-2 sticky right-0 bg-surface-850 group-hover:bg-surface-700 border-b border-l border-line transition-colors">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className="btn-ghost !py-1.5 !px-3"
                        onClick={() => {
                          setSelectedLeague(league);
                          setShowLeagueModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-secondary !py-1.5 !px-3"
                        onClick={() => handleManageClubs(league)}
                      >
                        Clubs
                      </button>
                      <button
                        type="button"
                        className="btn-danger !py-1.5 !px-3"
                        onClick={() => setLeagueToDelete(league)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {leagueToDelete && (
        <ConfirmDialog
          title="Delete league"
          message={`Delete “${leagueToDelete.name}”? This action cannot be undone.`}
          confirmLabel="Delete league"
          onConfirm={handleDeleteLeague}
          onCancel={() => setLeagueToDelete(null)}
          busy={loading}
        />
      )}

      {showLeagueModal && (
        <LeagueModal
          league={selectedLeague}
          onClose={() => {
            setShowLeagueModal(false);
            setSelectedLeague(null);
          }}
          onSubmit={selectedLeague ? handleEditLeague : handleAddLeague}
        />
      )}

      {showClubsModal && selectedLeague && (
        <LeagueClubsModal
          league={selectedLeague}
          clubs={clubs}
          onClose={() => {
            setShowClubsModal(false);
            setSelectedLeague(null);
          }}
          onAddClubs={handleAddClubsToLeague}
          onRemoveClubs={handleRemoveClubsFromLeague}
        />
      )}
    </div>
  );
};

export default LeaguesTab; 